/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { useState, useRef, useEffect } from 'react'
import { Settings, FolderOpen, Save, Play, RotateCcw, FileText, X, Menu, GitBranch, Share2, ChevronDown, Cpu, Undo, Redo, BookOpen, Coffee, Brain, GitCommit } from 'lucide-react'
import { jsPDF } from 'jspdf'
import hivemindLogo from '/hivemind-logo.svg'
import { useProjectStore } from '@/ui/stores/project-store'
import { useChatStore } from '@/ui/stores/chat-store'
import { useOrchestratorStore } from '@/ui/stores/orchestrator-store'
import { useAuth } from '@/ui/auth/AuthProvider'
import { CloudUpload } from 'lucide-react'
import { SettingsModal } from '@/ui/components/SettingsModal'
import { PeerModal } from '@/ui/components/PeerModal'
import { AddPeerModal } from '@/ui/components/AddPeerModal'
import { PeerSettingsModal } from '@/ui/components/PeerSettingsModal'
import { ProjectSetupModal } from '@/ui/components/ProjectSetupModal'
import { WorkspaceSetupModal } from '@/ui/components/WorkspaceSetupModal'
import { UserGuideModal } from '@/ui/components/UserGuideModal'
import { OnboardingModal } from '@/ui/components/OnboardingModal'
import { FileViewModal } from '@/ui/components/FileViewModal'
import { LeftSidebar } from '@/ui/components/LeftSidebar'
import { RightSidebar } from '@/ui/components/RightSidebar'
import { CenterPanel } from '@/ui/components/CenterPanel'
import { useResizing } from '@/ui/hooks/useResizing'
import { usePeerManagement } from '@/ui/hooks/usePeerManagement'
import { useInflation } from '@/ui/hooks/useInflation'
import { useJudgeSettings } from '@/ui/hooks/useJudgeSettings'
import { parseApiError } from '@/lib/api-errors'
import { ErrorModal } from '@/ui/components/ErrorModal'
import { GitHubRepoBrowser } from '@/ui/components/GitHubRepoBrowser'
import { GitHubSyncButton } from '@/ui/components/GitHubSyncButton'
import { GitHubDuplicateModal } from '@/ui/components/GitHubDuplicateModal'
import { GitCommitModal } from '@/ui/components/GitCommitModal'
import { GitToolConfirmModal } from '@/ui/components/GitToolConfirmModal'
import { FileToolConfirmModal } from '@/ui/components/FileToolConfirmModal'
import { AuthButton } from '@/ui/components/AuthButton'
import { LoginScreen } from '@/ui/auth/LoginScreen'
import { FirstRunWizard } from '@/ui/components/FirstRunWizard'
import { ShareModal } from '@/ui/components/ShareModal'
import type { GitToolAction } from '@/providers/git-tools'
import { saveWorkspaceHandle, getDirectoryHandle } from '@/lib/storage'
import { hasAnyApiKey, getApiKey, syncKeysFromRemote } from '@/lib/api-keys'
import { readFile, fixDoubleEncoding } from '@/lib/filesystem'

function App() {
  const { user } = useAuth()
  // UI state
  const [activeTab, setActiveTab] = useState('Chat')
  const [chatInput, setChatInput] = useState('')
  const [showSettings, setShowSettings] = useState(false)
  const [showUserGuide, setShowUserGuide] = useState(false)
  const [showProjectMenu, setShowProjectMenu] = useState(false)
  const [showNewProject, setShowNewProject] = useState(false)
  const [showLoadRepo, setShowLoadRepo] = useState(false)
  const [showGitHubBrowser, setShowGitHubBrowser] = useState(false)
  const [newProjectName, setNewProjectName] = useState('')
  const [showAddPeer, setShowAddPeer] = useState(false)
  const [autoPeerReview, setAutoPeerReview] = useState(false) // Auto review disabled by default (Brainstorm mode is conversational)
  const [includeDocs, setIncludeDocs] = useState(true) // Include attached docs in peer reviews by default
  const [isExportingPDF, setIsExportingPDF] = useState(false)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [errorDetails, setErrorDetails] = useState<{
    title: string
    message: string
    type: 'payment' | 'api_key' | 'rate_limit' | 'model_error' | 'generic'
    actionUrl?: string
    actionLabel?: string
  } | null>(null)
  const [showWorkspaceSetup, setShowWorkspaceSetup] = useState(false)
  const [workspaceChecked, setWorkspaceChecked] = useState(false)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [hasSeenOnboarding, setHasSeenOnboarding] = useState(false)
  const [fileViewModal, setFileViewModal] = useState<{
    filePath: string
    content: string
    highlightLines?: { start: number; end: number }
  } | null>(null)
  const [fileListRefreshTrigger, setFileListRefreshTrigger] = useState(0)
  const [highlightFile, setHighlightFile] = useState<string | null>(null)
  const [showGitCommit, setShowGitCommit] = useState(false)
  const [showFirstRunWizard, setShowFirstRunWizard] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)

  // Check for keys on login
  useEffect(() => {
    const checkKeys = async () => {
      if (user) {
        // Try to sync from remote first
        await syncKeysFromRemote(user.uid)

        // Then check if we have any keys
        if (!hasAnyApiKey()) {
          setShowFirstRunWizard(true)
        }
      }
    }
    checkKeys()
  }, [user])

  // Handler to open file with highlighted lines (for RAG citations)
  const handleOpenFile = async (filePath: string, highlightLines: { start: number; end: number }) => {
    if (!currentProject) return

    try {
      let content: string | null = null

      // Try to get content from filesystem if it's a repo project
      if (currentProject.fileSystemPath) {
        const dirHandle = await getDirectoryHandle(currentProject.id)
        if (dirHandle) {
          content = await readFile(dirHandle, filePath)
        }
      } else {
        // Get content from project.files for non-repo projects
        const rawContent = currentProject.files[filePath] || null
        content = rawContent ? fixDoubleEncoding(rawContent) : null
      }

      if (content !== null) {
        setFileViewModal({ filePath, content, highlightLines })
      } else {
        console.error(`Failed to read file: ${filePath}`)
      }
    } catch (error) {
      console.error(`Failed to open file ${filePath}:`, error)
    }
  }

  // Refs
  const containerRef = useRef<HTMLDivElement>(null)
  const previewRef = useRef<HTMLDivElement>(null)

  // Stores
  const {
    currentProject,
    projects,
    loadProjects,
    createProject,
    loadRepoAsProject,
    loadGitHubRepoAsProject,
    openProject,
    saveCurrentProject,
    saveToCloud,
    saveThread,
    updateFile,
    deleteFile,
    updateProjectSettings,
    githubDuplicateInfo,
    setGitHubDuplicateInfo,
    handleGitHubDuplicateAction
  } = useProjectStore()
  const { messages, isLoading, error: chatError, sendMessage, stopStreaming, setOnResponseComplete, setOnSaveCompiledDocument, loadMessages, setModel: setOrchestratorModel, resetToDefault: resetChat, mode, sessionCost: chatSessionCost, sessionTokens: chatSessionTokens, lastRequestLatency: chatLastLatency, pendingGitAction, setPendingGitAction, confirmGitAction, cancelGitAction, sendGitToolResult, pendingFileAction, setPendingFileAction, confirmFileAction, cancelFileAction, sendFileToolResult, updateStreamingMessage } = useChatStore()
  const { peerResponses, judgeResult, isOrchestrating, runOrchestration, metricsHistory, cumulativeCost: peerCumulativeCost, cumulativeTokens: peerCumulativeTokens, loadPeerResponses, error: orchestratorError, clearError: clearOrchestratorError, resetToDefault: resetOrchestrator } = useOrchestratorStore()

  // Custom hooks
  const {
    leftWidth,
    rightWidth,
    setIsResizingLeft,
    setIsResizingRight,
  } = useResizing(containerRef)

  // Right panel collapse state
  const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState(false)

  // Auto-open right panel when switching to peer-review mode
  useEffect(() => {
    if (mode === 'peer-review' && isRightPanelCollapsed) {
      setIsRightPanelCollapsed(false)
    }
  }, [mode, isRightPanelCollapsed])

  const peers = currentProject?.settings?.peers || []

  const {
    peerHeights,
    resizingPeer,
    expandedPeer,
    draggedPeer,
    editingPeerId,
    setPeerHeights,
    setResizingPeer,
    setExpandedPeer,
    setDraggedPeer,
    setEditingPeerId,
  } = usePeerManagement(peers)

  const {
    inflatedMarkdown,
    isInflating,
    selectedInflatedDoc,
    inflatedDocs,
    copiedInflate,
    copiedPreview,
    hasInflatedCurrentThread,
    setInflatedMarkdown,
    setIsInflating,
    setSelectedInflatedDoc,
    setInflatedDocs,
    setCopiedInflate,
    setCopiedPreview,
    setHasInflatedCurrentThread,
  } = useInflation()

  const {
    judgeAutoSend,
    judgeThreshold,
    judgeNotes,
    isEditingThreshold,
    approvalSent,
    setJudgeAutoSend,
    setJudgeThreshold,
    setJudgeNotes,
    setIsEditingThreshold,
    setApprovalSent,
  } = useJudgeSettings()

  // Ensure project has default model settings and peers
  useEffect(() => {
    const initializeProjectDefaults = async () => {
      if (currentProject) {
        let needsUpdate = false
        const updates: any = { ...currentProject.settings }

        // Add default models if missing
        if (!currentProject.settings?.models) {
          console.log('🔧 Adding default models to project')
          updates.models = {
            orchestrator: 'anthropic/claude-3-5-sonnet-20241022',
            gemini: 'google/gemini-2.5-pro',
            claude: 'anthropic/claude-3-5-sonnet-20241022',
            grok: 'x-ai/grok-4',
            judge: 'anthropic/claude-3-5-sonnet-20241022',
          }
          needsUpdate = true
        } else if (!currentProject.settings.models.orchestrator) {
          // Add orchestrator model if missing from existing settings
          console.log('🔧 Adding orchestrator model to project')
          updates.models = {
            ...currentProject.settings.models,
            orchestrator: 'anthropic/claude-3-5-sonnet-20241022',
          }
          needsUpdate = true
        }

        // Add default peers if missing
        if (!currentProject.settings?.peers || currentProject.settings.peers.length === 0) {
          console.log('🔧 Adding 3 default peers to project')
          updates.peers = [
            {
              id: 'peer-1',
              personaId: 'code-reviewer',
              personaName: 'Code Reviewer',
              model: 'google/gemini-2.5-pro',
              systemPrompt: `You are Code_Reviewer in the HiveMind system. Respond in MOTH format ONLY.

[CRITIQUE]
persona:Code_Reviewer
role:Code Quality Analyst

[OVERALL_ASSESSMENT]
summary:<1-2 sentences>
current_score:<average of all scores below>

[SCORES]
correctness:{score=0.0-1.0; reason=<why>}
quality:{score=0.0-1.0; reason=<why>}
clarity:{score=0.0-1.0; reason=<why>}

[STRENGTHS]
- <strength>

[WEAKNESSES]
- <weakness>

[RECOMMENDATIONS]
priority:high; description:<what to fix>

[IMPROVEMENT_PATH]
to_reach_90_percent:
- <specific action>
estimated_score_after_changes:<0.0-1.0>

NO markdown. NO prose. MOTH format ONLY.`,
              enabled: true,
              collapsed: false,
              color: 'text-purple-400',
              icon: 'gemini',
            },
            {
              id: 'peer-2',
              personaId: 'creative-writer',
              personaName: 'Creative Writer',
              model: 'anthropic/claude-sonnet-4.5',
              systemPrompt: `You are Creative_Writer in the HiveMind system. Respond in MOTH format ONLY.

[CRITIQUE]
persona:Creative_Writer
role:Creative Writing Specialist

[OVERALL_ASSESSMENT]
summary:<1-2 sentences>
current_score:<average of all scores below>

[SCORES]
originality:{score=0.0-1.0; reason=<why>}
engagement:{score=0.0-1.0; reason=<why>}
clarity:{score=0.0-1.0; reason=<why>}

[STRENGTHS]
- <strength>

[WEAKNESSES]
- <weakness>

[RECOMMENDATIONS]
priority:high; description:<what to improve>

[IMPROVEMENT_PATH]
to_reach_90_percent:
- <specific action>
estimated_score_after_changes:<0.0-1.0>

NO markdown. NO prose. MOTH format ONLY.`,
              enabled: true,
              collapsed: false,
              color: 'text-orange-400',
              icon: 'claude',
            },
            {
              id: 'peer-3',
              personaId: 'innovation-advisor',
              personaName: 'Innovation Advisor',
              model: 'x-ai/grok-4',
              systemPrompt: `You are Innovation_Advisor in the HiveMind system. Respond in MOTH format ONLY.

[CRITIQUE]
persona:Innovation_Advisor
role:Innovation & Strategy Analyst

[OVERALL_ASSESSMENT]
summary:<1-2 sentences>
current_score:<average of all scores below>

[SCORES]
innovation:{score=0.0-1.0; reason=<why>}
feasibility:{score=0.0-1.0; reason=<why>}
impact:{score=0.0-1.0; reason=<why>}

[STRENGTHS]
- <strength>

[WEAKNESSES]
- <weakness>

[RECOMMENDATIONS]
priority:high; description:<what to enhance>

[IMPROVEMENT_PATH]
to_reach_90_percent:
- <specific action>
estimated_score_after_changes:<0.0-1.0>

NO markdown. NO prose. MOTH format ONLY.`,
              enabled: true,
              collapsed: false,
              color: 'text-green-400',
              icon: 'grok',
            },
          ]
          needsUpdate = true
        } else {
          console.log(`✅ Project has ${currentProject.settings.peers.length} peers already`)
        }

        if (needsUpdate) {
          console.log('💾 Saving project defaults:', {
            hasPeers: !!updates.peers,
            peerCount: updates.peers?.length,
            hasModels: !!updates.models
          })
          await updateProjectSettings(updates)
          console.log('✅ Project defaults saved successfully')
        }
      }
    }

    initializeProjectDefaults()
  }, [currentProject?.id, updateProjectSettings])

  // Helper to toggle peer collapse
  const togglePeerCollapse = (peerId: string) => {
    const updatedPeers = peers.map(p =>
      p.id === peerId ? { ...p, collapsed: !p.collapsed } : p
    )
    updateProjectSettings({
      ...currentProject?.settings,
      peers: updatedPeers
    })
  }

  // Helper to toggle peer enabled
  const togglePeerEnabled = (peerId: string) => {
    const updatedPeers = peers.map(p =>
      p.id === peerId ? { ...p, enabled: !p.enabled } : p
    )
    updateProjectSettings({
      ...currentProject?.settings,
      peers: updatedPeers
    })
  }

  // Helper to handle peer drop (reordering)
  const handlePeerDrop = (targetPeerId: string, draggedPeerId: string) => {
    const draggedIndex = peers.findIndex(p => p.id === draggedPeerId)
    const targetIndex = peers.findIndex(p => p.id === targetPeerId)

    if (draggedIndex === -1 || targetIndex === -1) return

    const newPeers = [...peers]
    const [draggedPeer] = newPeers.splice(draggedIndex, 1)
    newPeers.splice(targetIndex, 0, draggedPeer)

    updateProjectSettings({
      ...currentProject?.settings,
      peers: newPeers
    })
  }

  // Helper to send peer response back to orchestrator
  const sendPeerToOrchestrator = (peerName: string, content: string) => {
    const message = `[PEER_FEEDBACK]\nfrom:${peerName}\n\n${content}`
    sendMessage(message)
  }

  // Helper to send judge result to orchestrator
  const sendJudgeToOrchestrator = () => {
    if (!judgeResult) return
    const message = `[JUDGE_APPROVAL]\nscore:${judgeResult.score}\n\n[REASONING]\n${judgeResult.reasoning}`
    sendMessage(message)
  }

  // Helper to request revisions from orchestrator
  const requestRevisions = () => {
    if (!judgeResult) return
    const message = `[REVISION_REQUEST]\nscore:${judgeResult.score}\nstatus:needs_improvement\n\n[JUDGE_FEEDBACK]\n${judgeResult.reasoning}\n\nPlease revise based on the judge's feedback above.`
    sendMessage(message)
  }

  // Helper to copy judge result
  const copyJudgeResult = async () => {
    if (!judgeResult) return
    const content = `[JUDGE_RESULT]\nscore:${judgeResult.score}\n\n[REASONING]\n${judgeResult.reasoning}`
    try {
      await navigator.clipboard.writeText(content)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  // Helper to inflate judge result
  const inflateJudgeResult = () => {
    if (!judgeResult) return
    const content = `[JUDGE_RESULT]\nscore:${judgeResult.score}\n\n[REASONING]\n${judgeResult.reasoning}`
    inflatePeerResponse('Judge Result', content)
  }

  // Helper to handle approve
  const handleApprove = async () => {
    if (!judgeResult) return

    // Send approval to orchestrator
    const message = `[JUDGE_APPROVAL]\nscore:${judgeResult.score}\nthreshold:${judgeThreshold}\n\n[REASONING]\n${judgeResult.reasoning}\n\n[NOTES]\n${judgeNotes || 'No additional notes'}`
    await sendMessage(message)
    setApprovalSent(true)
  }

  // Helper to copy inflated markdown
  const copyInflatedMarkdown = async () => {
    if (!inflatedMarkdown) return
    try {
      await navigator.clipboard.writeText(inflatedMarkdown)
      setCopiedInflate(true)
      setTimeout(() => setCopiedInflate(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  // Helper to export PDF with vector text (emoji stripped for compatibility)
  const exportPDF = async () => {
    if (!inflatedMarkdown || !currentProject) return

    setIsExportingPDF(true)
    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      })

      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const margin = 15
      const maxWidth = pageWidth - (margin * 2)
      let yPosition = margin

      const checkAndAddPage = (requiredSpace: number) => {
        if (yPosition + requiredSpace > pageHeight - margin) {
          doc.addPage()
          yPosition = margin
          return true
        }
        return false
      }

      // Strip emoji and problematic Unicode characters
      const cleanText = (text: string) => {
        return text
          // Common Unicode replacements
          .replace(/[\u2018\u2019]/g, "'")
          .replace(/[\u201C\u201D]/g, '"')
          .replace(/[\u2013\u2014]/g, '-')
          .replace(/[\u2026]/g, '...')
          .replace(/[\u00A0]/g, ' ')
          // Strip all emoji ranges
          .replace(/[\u{1F300}-\u{1F9FF}]/gu, '')
          .replace(/[\u{2600}-\u{26FF}]/gu, '')
          .replace(/[\u{2700}-\u{27BF}]/gu, '')
          .replace(/[\u{1F000}-\u{1F6FF}]/gu, '')
          .replace(/[\u{1F900}-\u{1F9FF}]/gu, '')
          // Remove remaining non-ASCII
          .replace(/[^\x00-\x7F]/g, '')
          .replace(/\s+/g, ' ')
          .trim()
      }

      const lines = inflatedMarkdown.split('\n')

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim()
        if (!line) {
          yPosition += 3
          continue
        }

        if (line.startsWith('# ')) {
          checkAndAddPage(15)
          doc.setFontSize(24)
          doc.setFont('helvetica', 'bold')
          const text = cleanText(line.substring(2))
          const splitText = doc.splitTextToSize(text, maxWidth)
          doc.text(splitText, margin, yPosition)
          yPosition += (splitText.length * 10) + 6
        } else if (line.startsWith('## ')) {
          checkAndAddPage(12)
          doc.setFontSize(18)
          doc.setFont('helvetica', 'bold')
          const text = cleanText(line.substring(3))
          const splitText = doc.splitTextToSize(text, maxWidth)
          doc.text(splitText, margin, yPosition)
          yPosition += (splitText.length * 8) + 5
        } else if (line.startsWith('### ')) {
          checkAndAddPage(10)
          doc.setFontSize(14)
          doc.setFont('helvetica', 'bold')
          const text = cleanText(line.substring(4))
          const splitText = doc.splitTextToSize(text, maxWidth)
          doc.text(splitText, margin, yPosition)
          yPosition += (splitText.length * 6) + 4
        } else if (line.startsWith('- ') || line.startsWith('* ')) {
          checkAndAddPage(8)
          doc.setFontSize(11)
          doc.setFont('helvetica', 'normal')
          const text = cleanText(line.substring(2))
          const splitText = doc.splitTextToSize(text, maxWidth - 5)
          doc.text('•', margin, yPosition)
          doc.text(splitText, margin + 5, yPosition)
          yPosition += (splitText.length * 5) + 2
        } else if (line.match(/^\d+\. /)) {
          checkAndAddPage(8)
          doc.setFontSize(11)
          doc.setFont('helvetica', 'normal')
          const match = line.match(/^(\d+)\. (.*)/)
          if (match) {
            const num = match[1]
            const text = cleanText(match[2])
            const splitText = doc.splitTextToSize(text, maxWidth - 8)
            doc.text(`${num}.`, margin, yPosition)
            doc.text(splitText, margin + 8, yPosition)
            yPosition += (splitText.length * 5) + 2
          }
        } else if (line.startsWith('```')) {
          checkAndAddPage(8)
          doc.setFontSize(9)
          doc.setFont('courier', 'normal')
          i++
          while (i < lines.length && !lines[i].trim().startsWith('```')) {
            const codeLine = cleanText(lines[i])
            checkAndAddPage(5)
            const splitText = doc.splitTextToSize(codeLine, maxWidth)
            doc.text(splitText, margin + 3, yPosition)
            yPosition += (splitText.length * 4) + 1
            i++
          }
          yPosition += 3
        } else {
          checkAndAddPage(8)
          doc.setFontSize(11)
          doc.setFont('helvetica', 'normal')
          let text = cleanText(line)
            .replace(/\*\*(.+?)\*\*/g, '$1')
            .replace(/\*(.+?)\*/g, '$1')
            .replace(/`(.+?)`/g, '$1')
          const splitText = doc.splitTextToSize(text, maxWidth)
          doc.text(splitText, margin, yPosition)
          yPosition += (splitText.length * 5) + 3
        }
      }

      doc.save(`${currentProject.name}-preview.pdf`)

    } catch (err) {
      console.error('Failed to export PDF:', err)
    } finally {
      setIsExportingPDF(false)
    }
  }

  // Save orchestrator chat to file
  const saveOrchestratorChat = async () => {
    if (!currentProject) return

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)
    const fileName = `chats/orchestrator-${timestamp}.moth`

    let content = `[ORCHESTRATOR_CHAT]\ntimestamp:${new Date().toISOString()}\nproject:${currentProject.name}\n\n`

    messages.forEach((msg) => {
      if (msg.role === 'system') return
      content += `[MESSAGE]\nrole:${msg.role}\ntimestamp:${new Date(msg.timestamp).toISOString()}\n\n${msg.content}\n\n`
    })

    updateFile(fileName, content)
    await saveCurrentProject()
  }

  // Save peer chat to file
  const savePeerChat = async (peerId: string, peerName: string, content: string) => {
    if (!currentProject || !content) return

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)
    const fileName = `peers/${peerId}-${timestamp}.moth`

    const fileContent = `[PEER_RESPONSE]\npeer:${peerName}\ntimestamp:${new Date().toISOString()}\nproject:${currentProject.name}\n\n${content}`

    updateFile(fileName, fileContent)
    await saveCurrentProject()
  }

  // Save judge result to file
  const saveJudgeResult = async () => {
    if (!currentProject || !judgeResult) return

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)
    const fileName = `judge/result-${timestamp}.moth`

    const content = `[JUDGE_RESULT]\ntimestamp:${new Date().toISOString()}\nproject:${currentProject.name}\nscore:${judgeResult.score}\n\n[REASONING]\n${judgeResult.reasoning}`

    updateFile(fileName, content)
    await saveCurrentProject()
  }

  // Copy peer response to clipboard
  const copyPeerResponse = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content)
    } catch (error) {
      console.error('Failed to copy:', error)
    }
  }

  // Save individual chat message
  const saveChatMessage = async (message: { id: string; role: string; content: string; timestamp: number }) => {
    if (!currentProject) return

    const timestamp = new Date(message.timestamp).toISOString().replace(/[:.]/g, '-').slice(0, -5)
    const fileName = `chats/orchestrator-msg-${timestamp}.moth`

    const content = `[ORCHESTRATOR_MESSAGE]\ntimestamp:${new Date(message.timestamp).toISOString()}\nproject:${currentProject.name}\nrole:${message.role}\n\n${message.content}`

    updateFile(fileName, content)
    await saveCurrentProject()
  }

  // Inflate single peer response
  const inflatePeerResponse = async (peerName: string, content: string) => {
    if (!currentProject) return

    setIsInflating(true)
    setInflatedMarkdown('# 🔄 Inflating MOTH to Markdown\n\n**Status:** Preparing MOTH content...')
    setActiveTab('Inflate')

    try {
      const mothContent = `[PEER_RESPONSE]\npeer:${peerName}\ntimestamp:${new Date().toISOString()}\nproject:${currentProject.name}\n\n${content}`

      setInflatedMarkdown('# 🔄 Inflating MOTH to Markdown\n\n**Status:** Uploading MOTH packet to LLM...\n\n*This may take a moment...*')

      const inflatePrompt = `You are a MOTH (Minimal Overhead Technical Hierarchy) format translator. Convert this compact MOTH peer response into beautiful, human-friendly Markdown documentation.

Guidelines:
- Convert MOTH sections into clear Markdown headings
- Expand terse notation into full sentences
- Add context and explanations where helpful
- Use proper Markdown formatting (headers, lists, code blocks, emphasis)
- Make it narrative and engaging
- Preserve all technical details

Here is the MOTH content to inflate:

${mothContent}

Please convert this into beautiful, human-friendly Markdown documentation.`

      const inflateModel = currentProject.settings?.models?.claude || 'anthropic/claude-3-5-sonnet-20241022'

      setInflatedMarkdown('# 🔄 Inflating MOTH to Markdown\n\n**Status:** Sending request to LLM...\n\n**Model:** ' + inflateModel + '\n\n*Waiting for response...*')

      const { getApiKey: getRuntimeApiKey } = await import('@/lib/api-keys')
      const orKey = getRuntimeApiKey('openrouter')
      if (!orKey) throw new Error('OpenRouter API key not configured')

      const response = await fetch('https://openrouter.ai/api/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${orKey}`,
          'HTTP-Referer': window.location.origin,
          'X-Title': 'HiveMind',
        },
        body: JSON.stringify({
          model: inflateModel,
          messages: [{ role: 'user', content: inflatePrompt }],
          stream: true,
        }),
      })

      if (!response.ok) {
        throw new Error(`Inflate failed: ${response.statusText}`)
      }

      setInflatedMarkdown('# 🔄 Inflating MOTH to Markdown\n\n**Status:** Receiving response from LLM...\n\n---\n\n')

      // Stream the response
      const reader = response.body?.getReader()
      const decoder = new TextDecoder()
      let markdown = ''

      if (reader) {
        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          const chunk = decoder.decode(value)
          const lines = chunk.split('\n')

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6)
              if (data === '[DONE]') continue

              try {
                const parsed = JSON.parse(data)
                const delta = parsed.choices[0]?.delta?.content
                if (delta) {
                  markdown += delta
                  setInflatedMarkdown(markdown)
                }
              } catch (e) {
                // Skip invalid JSON
              }
            }
          }
        }
      }

      if (!markdown) {
        throw new Error('No content received from LLM')
      }

      setInflatedMarkdown(markdown)

      // Save inflated doc
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)
      const fileName = `inflated/${peerName.toLowerCase().replace(/\s+/g, '-')}-${timestamp}.md`

      updateFile(fileName, markdown)
      await saveCurrentProject()

      setInflatedDocs(prev => ({ ...prev, [fileName]: markdown }))
      setSelectedInflatedDoc(fileName)
    } catch (error) {
      console.error('Inflate error:', error)
      setInflatedMarkdown(`# ❌ Error\n\nFailed to inflate:\n\n${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setIsInflating(false)
    }
  }

  // Inflate MOTH to Markdown using LLM
  const handleInflate = async () => {
    if (!currentProject?.thread) {
      setInflatedMarkdown('# No Thread Data\n\nNo conversation to inflate.')
      return
    }

    // Check if we've already inflated the current thread
    if (hasInflatedCurrentThread && inflatedMarkdown) {
      return // Don't inflate again
    }

    setIsInflating(true)
    setInflatedMarkdown('# 🔄 Inflating Full Thread\n\n**Status:** Gathering MOTH content from thread...')

    try {
      // Gather all MOTH content from the thread
      let mothContent = `[PROJECT]\nname:${currentProject.name}\n\n`

      setInflatedMarkdown('# 🔄 Inflating Full Thread\n\n**Status:** Collecting orchestrator messages...')

      // Add orchestrator messages
      mothContent += `[ORCHESTRATOR_CHAT]\n`
      currentProject.thread.messages.forEach((msg) => {
        if (msg.role === 'system') return
        mothContent += `\n[MESSAGE]\nrole:${msg.role}\ntimestamp:${new Date(msg.timestamp).toISOString()}\n\n${msg.content}\n`
      })

      setInflatedMarkdown('# 🔄 Inflating Full Thread\n\n**Status:** Collecting peer responses...')

      // Add peer responses
      if (currentProject.thread.peerResponses.gemini) {
        mothContent += `\n\n[PEER_RESPONSE]\npeer:Gemini Technical Reviewer\n\n${currentProject.thread.peerResponses.gemini.content}\n`
      }
      if (currentProject.thread.peerResponses.claude) {
        mothContent += `\n\n[PEER_RESPONSE]\npeer:Claude Content Editor\n\n${currentProject.thread.peerResponses.claude.content}\n`
      }
      if (currentProject.thread.peerResponses.grok) {
        mothContent += `\n\n[PEER_RESPONSE]\npeer:Grok Innovation Advisor\n\n${currentProject.thread.peerResponses.grok.content}\n`
      }

      // Add judge result
      if (currentProject.thread.judgeResult) {
        mothContent += `\n\n[JUDGE_RESULT]\nscore:${currentProject.thread.judgeResult.score}\n\n[REASONING]\n${currentProject.thread.judgeResult.reasoning}\n`
      }

      setInflatedMarkdown('# 🔄 Inflating Full Thread\n\n**Status:** Preparing inflation prompt...')

      // Call LLM to inflate
      const inflatePrompt = `You are a MOTH (Minimal Overhead Technical Hierarchy) format translator. Your job is to convert compact MOTH format into beautiful, human-friendly Markdown documentation.

MOTH is a structured format optimized for LLM context windows. Your task is to expand it into readable, well-formatted Markdown that humans will enjoy reading.

Guidelines:
- Convert MOTH sections into clear Markdown headings
- Expand terse notation into full sentences
- Add context and explanations where helpful
- Use proper Markdown formatting (headers, lists, code blocks, emphasis)
- Make it narrative and engaging, not just a data dump
- Preserve all technical details and scores
- Add visual separators and structure

Here is the MOTH content to inflate:

${mothContent}

Please convert this into beautiful, human-friendly Markdown documentation.`

      // Use the orchestrator model (or default to Claude)
      const inflateModel = currentProject.settings?.models?.claude || 'anthropic/claude-3-5-sonnet-20241022'

      setInflatedMarkdown('# 🔄 Inflating Full Thread\n\n**Status:** Sending request to LLM...\n\n**Model:** ' + inflateModel + '\n\n*This may take a moment for large threads...*')

      const { getApiKey: getRuntimeApiKey2 } = await import('@/lib/api-keys')
      const orKey2 = getRuntimeApiKey2('openrouter')
      if (!orKey2) throw new Error('OpenRouter API key not configured')

      const response = await fetch('https://openrouter.ai/api/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${orKey2}`,
          'HTTP-Referer': window.location.origin,
          'X-Title': 'HiveMind',
        },
        body: JSON.stringify({
          model: inflateModel,
          messages: [
            {
              role: 'user',
              content: inflatePrompt,
            },
          ],
          stream: true,
        }),
      })

      if (!response.ok) {
        throw new Error(`Inflate failed: ${response.statusText}`)
      }

      setInflatedMarkdown('# 🔄 Inflating Full Thread\n\n**Status:** Receiving response from LLM...\n\n---\n\n')

      // Stream the response
      const reader = response.body?.getReader()
      const decoder = new TextDecoder()
      let markdown = ''

      if (reader) {
        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          const chunk = decoder.decode(value)
          const lines = chunk.split('\n')

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6)
              if (data === '[DONE]') continue

              try {
                const parsed = JSON.parse(data)
                const delta = parsed.choices[0]?.delta?.content
                if (delta) {
                  markdown += delta
                  setInflatedMarkdown(markdown)
                }
              } catch (e) {
                // Skip invalid JSON
              }
            }
          }
        }
      }

      if (!markdown) {
        throw new Error('No content received from LLM')
      }

      setInflatedMarkdown(markdown)

      // Save inflated doc to project files
      if (currentProject) {
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)
        const fileName = `inflated/doc-${timestamp}.md`

        updateFile(fileName, markdown)
        await saveCurrentProject()

        // Update local state
        setInflatedDocs(prev => ({
          ...prev,
          [fileName]: markdown,
        }))
        setSelectedInflatedDoc(fileName)
        setHasInflatedCurrentThread(true)
      }
    } catch (error) {
      console.error('Inflate error:', error)
      setInflatedMarkdown(`# Error\n\nFailed to inflate MOTH content:\n\n${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setIsInflating(false)
    }
  }

  // Load a specific inflated doc
  const handleLoadInflatedDoc = (fileName: string) => {
    setSelectedInflatedDoc(fileName)
    setInflatedMarkdown(inflatedDocs[fileName] || '')
  }

  // Handle git tool confirmation
  const handleConfirmGitAction = async (editedAction?: GitToolAction) => {
    // Use edited action if provided, otherwise use pending action
    const actionToExecute = editedAction || pendingGitAction

    if (!actionToExecute || !currentProject) {
      console.warn('[App] No git action or no current project')
      return
    }

    try {
      console.log('[App] Executing git action:', actionToExecute)

      if (actionToExecute.type === 'commit_and_push') {
        // Execute commit and push
        const { commitAndPush } = await import('@/lib/git-operations')

        const result = await commitAndPush(
          actionToExecute.repoUrl!,
          actionToExecute.branch!,
          actionToExecute.commitMessage!,
          actionToExecute.files!,
          (message) => console.log('[Git]', message)
        )

        if (result.success) {
          console.log('[App] Git operation successful:', result.commitHash)

          // Log commit to .hivemind/commit-log.json
          try {
            const commitLogPath = '.hivemind/commit-log.json'
            let commitLog: any = { version: '1.0', commits: [] }

            // Try to read existing commit log
            if (currentProject.files[commitLogPath]) {
              try {
                commitLog = JSON.parse(currentProject.files[commitLogPath])
              } catch (e) {
                console.warn('[App] Failed to parse commit log, creating new one')
              }
            }

            // Add new commit
            commitLog.commits.push({
              sha: result.commitHash,
              fullSha: result.commitHash, // Will be full SHA when available
              message: actionToExecute.commitMessage,
              branch: actionToExecute.branch,
              repoUrl: actionToExecute.repoUrl,
              files: actionToExecute.files!.map(f => f.path),
              timestamp: new Date().toISOString(),
              author: 'HiveMind',
            })

            // Update the file
            updateFile(commitLogPath, JSON.stringify(commitLog, null, 2))
            console.log('[App] Commit logged to .hivemind/commit-log.json')
          } catch (logError) {
            console.warn('[App] Failed to log commit:', logError)
            // Don't fail the commit if logging fails
          }

          // Clear the pending action
          confirmGitAction()

          // Send success result back to LLM
          await sendGitToolResult(
            true,
            `Successfully committed and pushed ${actionToExecute.files!.length} file(s) to ${actionToExecute.branch}.\n\nCommit hash: ${result.commitHash}\n\nYou can now continue with the next steps.`
          )
        } else {
          console.error('[App] Git operation failed:', result.message)

          // Send failure result back to LLM
          await sendGitToolResult(false, result.message || 'Unknown error occurred')

          // Clear the pending action
          cancelGitAction()
        }
      } else if (actionToExecute.type === 'create_repo') {
        // Execute repository creation
        const { createGitHubRepo, initializeAndPushRepo } = await import('@/lib/github-repo-create')

        const createResult = await createGitHubRepo({
          name: actionToExecute.repoName!,
          description: actionToExecute.repoDescription,
          private: actionToExecute.isPrivate,
          autoInit: false,
        })

        if (createResult.success && createResult.repoUrl) {
          console.log('[App] Repository created successfully:', createResult.repoUrl)

          // Initialize with project files
          const initResult = await initializeAndPushRepo(
            createResult.repoUrl,
            'main',
            currentProject.files,
            (message) => console.log('[Git]', message)
          )

          if (initResult.success) {
            // Update project with GitHub info
            const urlMatch = createResult.repoUrl.match(/github\.com\/([^\/]+)\/([^\/]+)/)
            if (urlMatch) {
              const [, owner, repo] = urlMatch
              const repoName = repo.replace(/\.git$/, '')

              useProjectStore.getState().updateProject({
                ...currentProject,
                github: {
                  url: createResult.repoUrl,
                  owner,
                  repo: repoName,
                  fullName: `${owner}/${repoName}`,
                  branch: 'main',
                  lastCommitSha: '',
                },
              })

              await saveCurrentProject()
            }

            // Clear the pending action
            confirmGitAction()

            // Send success result back to LLM
            await sendGitToolResult(
              true,
              `Successfully created repository "${pendingGitAction?.repoName}" and pushed ${Object.keys(currentProject.files).length} file(s).\n\nRepository URL: ${createResult.repoUrl}\n\nThe project is now linked to this GitHub repository. You can continue working with git operations.`
            )
          } else {
            console.error('[App] Failed to initialize repository:', initResult.error)

            // Send partial success result back to LLM
            await sendGitToolResult(
              false,
              `Repository "${pendingGitAction?.repoName}" was created, but failed to push initial files: ${initResult.error}\n\nRepository URL: ${createResult.repoUrl}\n\nYou can manually push files later.`
            )

            // Clear the pending action
            cancelGitAction()
          }
        } else {
          console.error('[App] Repository creation failed:', createResult.error)

          // Send failure result back to LLM
          await sendGitToolResult(false, createResult.error || 'Unknown error occurred')

          // Clear the pending action
          cancelGitAction()
        }
      }
    } catch (error) {
      console.error('[App] Git operation error:', error)

      const errorMessage = error instanceof Error ? error.message : String(error)

      // Send error result back to LLM
      await sendGitToolResult(false, errorMessage)

      // Clear the pending action
      cancelGitAction()
    }
  }

  // Handle git tool cancellation
  const handleCancelGitAction = () => {
    console.log('[App] Git action cancelled by user')
    cancelGitAction()
  }

  // Handle file tool confirmation
  const handleConfirmFileAction = async () => {
    if (!pendingFileAction || !currentProject) {
      console.warn('[App] No pending file action or no current project')
      return
    }

    try {
      console.log('[App] Executing file action:', pendingFileAction)

      if (pendingFileAction.type === 'create_file') {
        // Create new file
        const { path, content } = pendingFileAction
        if (!path || !content) {
          await sendFileToolResult(false, 'Missing file path or content')
          cancelFileAction()
          return
        }

        // Write to file system if this is a file system project
        if (currentProject.fileSystemPath) {
          const dirHandle = await getDirectoryHandle(currentProject.id)
          if (dirHandle) {
            const { writeFile: writeToFS } = await import('@/lib/filesystem')
            await writeToFS(dirHandle, path, content)
            console.log(`[App] File written to disk: ${path}`)
            // Trigger file list refresh
            setFileListRefreshTrigger(prev => prev + 1)
          }
        } else {
          // Add file to in-memory project (for non-filesystem projects)
          await updateFile(path, content)
          await saveCurrentProject()
        }

        confirmFileAction()
        await sendFileToolResult(true, `Successfully created file "${path}" (${content.length} characters).`)

      } else if (pendingFileAction.type === 'update_file') {
        // Update existing file
        const { path, newContent } = pendingFileAction
        if (!path || !newContent) {
          await sendFileToolResult(false, 'Missing file path or content')
          cancelFileAction()
          return
        }

        // Get old content (from disk if file system project, otherwise from memory)
        let oldContent: string | null = null
        if (currentProject.fileSystemPath) {
          const dirHandle = await getDirectoryHandle(currentProject.id)
          if (dirHandle) {
            oldContent = await readFile(dirHandle, path)
          }
        } else {
          oldContent = currentProject.files[path]
        }

        if (!oldContent) {
          await sendFileToolResult(false, `File "${path}" does not exist`)
          cancelFileAction()
          return
        }

        // Write to file system if this is a file system project
        if (currentProject.fileSystemPath) {
          const dirHandle = await getDirectoryHandle(currentProject.id)
          if (dirHandle) {
            const { writeFile: writeToFS } = await import('@/lib/filesystem')
            await writeToFS(dirHandle, path, newContent)
            console.log(`[App] File updated on disk: ${path}`)
            // Trigger file list refresh
            setFileListRefreshTrigger(prev => prev + 1)
          }
        } else {
          // Update file in in-memory project (for non-filesystem projects)
          await updateFile(path, newContent)
          await saveCurrentProject()
        }

        confirmFileAction()
        await sendFileToolResult(true, `Successfully updated file "${path}" (${oldContent.length} → ${newContent.length} characters).`)

      } else if (pendingFileAction.type === 'delete_file') {
        // Delete file
        const { path } = pendingFileAction
        if (!path) {
          await sendFileToolResult(false, 'Missing file path')
          cancelFileAction()
          return
        }

        // Check if file exists
        let fileExists = false
        if (currentProject.fileSystemPath) {
          const dirHandle = await getDirectoryHandle(currentProject.id)
          if (dirHandle) {
            const content = await readFile(dirHandle, path)
            fileExists = content !== null
          }
        } else {
          fileExists = !!currentProject.files[path]
        }

        if (!fileExists) {
          await sendFileToolResult(false, `File "${path}" does not exist`)
          cancelFileAction()
          return
        }

        // Delete from file system if this is a file system project
        if (currentProject.fileSystemPath) {
          const dirHandle = await getDirectoryHandle(currentProject.id)
          if (dirHandle) {
            const { deleteFile: deleteFromFS } = await import('@/lib/filesystem')
            await deleteFromFS(dirHandle, path)
            console.log(`[App] File deleted from disk: ${path}`)
            // Trigger file list refresh
            setFileListRefreshTrigger(prev => prev + 1)
          }
        } else {
          // Remove file from in-memory project
          await deleteFile(path)
          await saveCurrentProject()
        }

        confirmFileAction()
        await sendFileToolResult(true, `Successfully deleted file "${path}".`)

      } else if (pendingFileAction.type === 'rename_file') {
        // Rename/move file
        const { oldPath, newPath } = pendingFileAction
        if (!oldPath || !newPath) {
          await sendFileToolResult(false, 'Missing old path or new path')
          cancelFileAction()
          return
        }

        // Get file content and check existence
        let content: string | null = null
        if (currentProject.fileSystemPath) {
          const dirHandle = await getDirectoryHandle(currentProject.id)
          if (dirHandle) {
            content = await readFile(dirHandle, oldPath)
            // Check if new path already exists
            const newExists = await readFile(dirHandle, newPath)
            if (newExists) {
              await sendFileToolResult(false, `File "${newPath}" already exists`)
              cancelFileAction()
              return
            }
          }
        } else {
          content = currentProject.files[oldPath]
          if (currentProject.files[newPath]) {
            await sendFileToolResult(false, `File "${newPath}" already exists`)
            cancelFileAction()
            return
          }
        }

        if (!content) {
          await sendFileToolResult(false, `File "${oldPath}" does not exist`)
          cancelFileAction()
          return
        }

        // Rename file on file system if this is a file system project
        if (currentProject.fileSystemPath) {
          const dirHandle = await getDirectoryHandle(currentProject.id)
          if (dirHandle) {
            const { writeFile: writeToFS, deleteFile: deleteFromFS } = await import('@/lib/filesystem')
            // Create new file, then delete old
            await writeToFS(dirHandle, newPath, content)
            await deleteFromFS(dirHandle, oldPath)
            console.log(`[App] File renamed on disk: ${oldPath} -> ${newPath}`)
            // Trigger file list refresh
            setFileListRefreshTrigger(prev => prev + 1)
          }
        } else {
          // Rename file in in-memory project (delete old, create new)
          await deleteFile(oldPath)
          await updateFile(newPath, content)
          await saveCurrentProject()
        }

        confirmFileAction()
        await sendFileToolResult(true, `Successfully renamed file "${oldPath}" to "${newPath}".`)
      }
    } catch (error) {
      console.error('[App] File operation error:', error)
      const errorMessage = error instanceof Error ? error.message : String(error)
      await sendFileToolResult(false, errorMessage)
      cancelFileAction()
    }
  }

  // Handle file tool cancellation
  const handleCancelFileAction = () => {
    console.log('[App] File action cancelled by user')
    cancelFileAction()
  }

  // Monitor pendingGitToolCall from chat store and create GitToolAction
  const pendingGitToolCall = useChatStore((state) => state.pendingGitToolCall)

  useEffect(() => {
    const checkPendingGitToolCall = async () => {
      // Only create the action if we have a pending tool call and no action yet
      if (pendingGitToolCall && !pendingGitAction && currentProject) {
        console.log('[App] Detected pending git tool call:', pendingGitToolCall)

        const { args } = pendingGitToolCall

        // Determine git tool type from args
        if (args.name && args.description !== undefined) {
          // create_github_repo
          console.log('[App] Creating git action for create_github_repo')
          setPendingGitAction({
            type: 'create_repo',
            repoName: args.name,
            repoDescription: args.description || '',
            isPrivate: args.private ?? true, // Default to private (safer)
          })
        } else if (args.message) {
          // commit_and_push
          console.log('[App] Creating git action for commit_and_push')

          // Get repo URL and branch from project settings
          const repoUrl = currentProject.github?.url
          const branch = currentProject.github?.branch || 'main'

          if (!repoUrl) {
            console.error('[App] No GitHub repository URL found in project settings')
            // Send error back to LLM
            await sendGitToolResult(false, 'No GitHub repository is linked to this project. Please create a repository first using create_github_repo.')
            return
          }

          // Convert file paths to FileChange objects with content
          const fileChanges = args.files && args.files.length > 0
            ? args.files.map((filePath: string) => ({
              path: filePath,
              content: currentProject.files[filePath] || '',
            }))
            : Object.entries(currentProject.files).map(([path, content]) => ({
              path,
              content,
            }))

          setPendingGitAction({
            type: 'commit_and_push',
            commitMessage: args.message,
            repoUrl,
            branch,
            files: fileChanges,
          })
        }
      }
    }

    checkPendingGitToolCall()
  }, [pendingGitToolCall, currentProject, pendingGitAction, setPendingGitAction])

  // Monitor pendingFileToolCall from chat store and create FileToolAction
  const pendingFileToolCall = useChatStore((state) => state.pendingFileToolCall)

  useEffect(() => {
    const checkPendingFileToolCall = async () => {
      if (pendingFileToolCall && !pendingFileAction && currentProject) {
        console.log('[App] Detected pending file tool call:', pendingFileToolCall)

        const { args } = pendingFileToolCall
        const toolName = pendingFileToolCall.id.split('-')[0] // Extract tool name from ID if needed

        // Determine tool type from args
        if (args.path && args.content && !args.old_path) {
          // create_file or update_file
          const isUpdate = currentProject.files[args.path] !== undefined

          if (isUpdate) {
            setPendingFileAction({
              type: 'update_file',
              path: args.path,
              oldContent: currentProject.files[args.path],
              newContent: args.content,
            })
          } else {
            setPendingFileAction({
              type: 'create_file',
              path: args.path,
              content: args.content,
            })
          }
        } else if (args.path && !args.content && !args.old_path) {
          // delete_file
          setPendingFileAction({
            type: 'delete_file',
            path: args.path,
            content: currentProject.files[args.path],
          })
        } else if (args.old_path && args.new_path) {
          // rename_file
          setPendingFileAction({
            type: 'rename_file',
            oldPath: args.old_path,
            newPath: args.new_path,
            content: currentProject.files[args.old_path],
          })
        }
      }
    }

    checkPendingFileToolCall()
  }, [pendingFileToolCall, currentProject, pendingFileAction, setPendingFileAction])

  // Monitor pendingMemoryToolCall from chat store and execute immediately (no confirmation needed)
  const pendingMemoryToolCall = useChatStore((state) => state.pendingMemoryToolCall)

  useEffect(() => {
    const handleMemoryToolCall = async () => {
      if (pendingMemoryToolCall && currentProject) {
        console.log('[App] Detected pending memory tool call:', pendingMemoryToolCall)

        const { args } = pendingMemoryToolCall
        const { memory, category } = args

        try {
          // Get current memory file
          const memoryPath = '.hivemind/memory.md'
          let memoryContent = currentProject.files[memoryPath] || `# HiveMind Project Memory

> This file contains long-term memories and context for AI assistants working on this project.
> Memories are automatically added when you ask the AI to remember something.

## Project Overview

## User Preferences

## Architecture Decisions

## Important Context

## Technical Notes
`

          // Determine which section to add to based on category
          const sectionMap: Record<string, string> = {
            user_preference: '## User Preferences',
            project_context: '## Project Overview',
            technical_decision: '## Architecture Decisions',
            important_note: '## Important Context',
          }

          const sectionHeader = sectionMap[category] || '## Important Context'

          // Find the section and append the memory
          const timestamp = new Date().toISOString().split('T')[0]
          const memoryEntry = `- **${timestamp}**: ${memory}`

          // Find the section and insert after it
          const sectionIndex = memoryContent.indexOf(sectionHeader)
          if (sectionIndex !== -1) {
            // Find the next section or end of file
            const afterSection = memoryContent.substring(sectionIndex + sectionHeader.length)
            const nextSectionMatch = afterSection.match(/\n##\s/)
            const insertPosition = nextSectionMatch
              ? sectionIndex + sectionHeader.length + afterSection.indexOf(nextSectionMatch[0])
              : memoryContent.length

            // Insert the memory
            memoryContent =
              memoryContent.substring(0, insertPosition) +
              '\n' + memoryEntry +
              memoryContent.substring(insertPosition)
          } else {
            // Section doesn't exist, append at end
            memoryContent += `\n\n${sectionHeader}\n${memoryEntry}\n`
          }

          // Update the file
          updateFile(memoryPath, memoryContent)
          await saveCurrentProject()

          // Send success result back to LLM
          const resultMessage = `✓ Memory stored: "${memory}" (category: ${category})\n\nThis will be remembered across sessions and available to all AI assistants working on this project.`

          // Update the chat message
          const lastAssistantMessage = messages[messages.length - 1]
          if (lastAssistantMessage && lastAssistantMessage.role === 'assistant') {
            const currentContent = typeof lastAssistantMessage.content === 'string' ? lastAssistantMessage.content : ''
            updateStreamingMessage(lastAssistantMessage.id, currentContent.replace('⏳ Saving...', '✅ Saved!') + '\n\n' + resultMessage)
          }

          console.log('[App] Memory stored successfully')
        } catch (error) {
          console.error('[App] Failed to store memory:', error)

          // Send error result back to LLM
          const errorMessage = `✗ Failed to store memory: ${error instanceof Error ? error.message : 'Unknown error'}`

          // Update the chat message
          const lastAssistantMessage = messages[messages.length - 1]
          if (lastAssistantMessage && lastAssistantMessage.role === 'assistant') {
            const currentContent = typeof lastAssistantMessage.content === 'string' ? lastAssistantMessage.content : ''
            updateStreamingMessage(lastAssistantMessage.id, currentContent.replace('⏳ Saving...', '❌ Failed!') + '\n\n' + errorMessage)
          }
        } finally {
          // Clear the pending tool call
          useChatStore.setState({ pendingMemoryToolCall: null })
        }
      }
    }

    handleMemoryToolCall()
  }, [pendingMemoryToolCall, currentProject, updateFile, saveCurrentProject, messages])

  // Load inflated docs when switching to Inflate tab (but don't auto-inflate)
  useEffect(() => {
    if (activeTab === 'Inflate' && currentProject) {
      // Load existing inflated docs from project files
      const docs: Record<string, string> = {}
      Object.entries(currentProject.files).forEach(([path, content]) => {
        if (path.startsWith('inflated/') && path.endsWith('.md')) {
          docs[path] = content
        }
      })
      setInflatedDocs(docs)

      // Auto-select most recent doc if available
      const sortedDocs = Object.keys(docs).sort().reverse()
      if (sortedDocs.length > 0 && !selectedInflatedDoc) {
        setSelectedInflatedDoc(sortedDocs[0])
        setInflatedMarkdown(docs[sortedDocs[0]])
      }
    }
  }, [activeTab, currentProject])

  // Load projects on mount and auto-load last project
  useEffect(() => {
    const initializeApp = async () => {
      // Check if user has API keys configured - do this FIRST for immediate feedback
      const hasKeys = hasAnyApiKey()

      // If no keys, show onboarding immediately without waiting for projects
      if (!hasKeys) {
        console.log('No keys found, showing onboarding immediately')
        setShowOnboarding(true)
        setHasSeenOnboarding(false)
        // Still load projects in background
        loadProjects()
        return
      }

      // Load projects
      await loadProjects()

      // Try to auto-load last opened project
      const lastProjectId = localStorage.getItem('lastProjectId')
      console.log('Auto-loading last project:', lastProjectId)

      if (lastProjectId) {
        // User has API keys and a previous project - auto-load it
        console.log('Auto-loading last project...')
        await openProject(lastProjectId)
        setHasSeenOnboarding(true)
      } else {
        // Has keys but no last project (unlikely but possible)
        // Just show the main UI
        setHasSeenOnboarding(true)
      }
    }

    initializeApp()
  }, [])

  // Load thread data when project changes
  useEffect(() => {
    if (currentProject?.thread) {
      // Load chat messages
      loadMessages(currentProject.thread.messages)
      // Load peer responses and judge result
      loadPeerResponses(currentProject.thread.peerResponses, currentProject.thread.judgeResult)
    } else if (currentProject) {
      // New project without thread data - reset to defaults
      resetChat()
      resetOrchestrator()
    }

    // Load inflated docs from project files
    if (currentProject?.files) {
      const inflatedFiles: Record<string, string> = {}
      Object.entries(currentProject.files).forEach(([fileName, content]) => {
        if ((fileName.startsWith('inflated-') || fileName.startsWith('inflated/')) && fileName.endsWith('.md')) {
          inflatedFiles[fileName] = content
        }
      })
      setInflatedDocs(inflatedFiles)

      // Select the most recent inflated doc if available
      const sortedDocs = Object.keys(inflatedFiles).sort().reverse()
      if (sortedDocs.length > 0) {
        setSelectedInflatedDoc(sortedDocs[0])
        setInflatedMarkdown(inflatedFiles[sortedDocs[0]])
      } else {
        setSelectedInflatedDoc(null)
        setInflatedMarkdown('')
      }
    }

    // Reset inflate flag when project changes
    setHasInflatedCurrentThread(false)

    // Note: We no longer override the user's orchestrator model preference.
    // The user's selected model (from localStorage) takes priority over project settings.
    // Projects can still store their preferred model, but it won't override the user's choice.
  }, [currentProject?.id]) // Only trigger when project ID changes

  // Auto-save thread data when messages or peer responses change (debounced)
  useEffect(() => {
    if (currentProject && messages.length > 1) { // Skip initial system message
      // Debounce saves to avoid rapid successive writes that cause InvalidStateError
      const timeoutId = setTimeout(() => {
        saveThread(messages, peerResponses, judgeResult)
        // Also save entire project to IndexedDB
        saveCurrentProject()
        // Reset inflate flag when thread changes
        setHasInflatedCurrentThread(false)
      }, 500) // Wait 500ms after last change before saving

      return () => clearTimeout(timeoutId)
    }
  }, [messages, peerResponses, judgeResult])

  // Set up auto-send to peers after orchestrator response
  // Use refs to capture latest values without causing re-renders
  const latestValuesRef = useRef({ isOrchestrating, approvalSent, peers, currentProject, runOrchestration, includeDocs, messages, autoPeerReview, mode })

  useEffect(() => {
    latestValuesRef.current = { isOrchestrating, approvalSent, peers, currentProject, runOrchestration, includeDocs, messages, autoPeerReview, mode }
  })

  useEffect(() => {
    const handleResponseComplete = async (responseContent: string) => {
      const { isOrchestrating, approvalSent, peers, currentProject, runOrchestration, includeDocs, messages, autoPeerReview, mode } = latestValuesRef.current

      console.log('[App] handleResponseComplete called with:', {
        isOrchestrating,
        approvalSent,
        mode,
        autoPeerReview,
        peersCount: peers.length,
        hasContent: !!responseContent,
        contentLength: responseContent?.length
      })

      // Send to peers if:
      // 1. Not already orchestrating
      // 2. We have content
      // 3. Approval wasn't just sent
      // 4. We're in peer-review mode (manual approval via "Approve & Send to Peers" button)
      //    OR auto peer review is enabled (auto-send after orchestrator response)
      if (!isOrchestrating && responseContent && !approvalSent && (mode === 'peer-review' || autoPeerReview)) {
        console.log('[App] ✅ Conditions met, sending to peers...')
        // Get attached files from the last user message if includeDocs is enabled
        let contextFiles: string | undefined
        if (includeDocs) {
          // Find the most recent user message with attachedFiles
          const lastUserMessage = [...messages].reverse().find(m => m.role === 'user' && m.attachedFiles && m.attachedFiles.length > 0)
          if (lastUserMessage?.attachedFiles) {
            contextFiles = lastUserMessage.attachedFiles
              .map(file => `[File: ${file.path}]\n\`\`\`\n${file.content}\n\`\`\``)
              .join('\n\n')
          }
        }

        await runOrchestration(
          responseContent,
          peers,
          currentProject?.files['rubric.moth'],
          currentProject?.settings?.models?.judge,
          contextFiles,
          currentProject?.thread?.peerHistory
        )
      }
      // Note: Not sending to peers is normal in brainstorm mode or when auto-review is off
    }

    setOnResponseComplete(handleResponseComplete)
    // Only run once on mount - the ref keeps values fresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Set up compiled document save handler
  useEffect(() => {
    const handleSaveCompiledDocument = async (document: string) => {
      if (!currentProject) return

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0]
      const filename = `brainstorm-review-${timestamp}.moth`

      // Save to project files
      await updateFile(filename, document)
      console.log(`[App] Saved compiled review document: ${filename}`)
    }

    setOnSaveCompiledDocument(handleSaveCompiledDocument)
  }, [currentProject, updateFile, setOnSaveCompiledDocument])

  // Watch for orchestrator errors and show error modal
  useEffect(() => {
    if (orchestratorError) {
      const parsed = parseApiError(orchestratorError)
      setErrorDetails({
        title: parsed.title,
        message: parsed.message,
        type: parsed.type,
        actionUrl: parsed.actionUrl,
        actionLabel: parsed.actionLabel,
      })
      setShowErrorModal(true)
      clearOrchestratorError()
    }
  }, [orchestratorError, clearOrchestratorError])

  // Watch for chat errors and show error modal
  useEffect(() => {
    if (chatError) {
      const parsed = parseApiError({ message: chatError })
      setErrorDetails({
        title: parsed.title,
        message: parsed.message,
        type: parsed.type,
        actionUrl: parsed.actionUrl,
        actionLabel: parsed.actionLabel,
      })
      setShowErrorModal(true)
    }
  }, [chatError])

  // Auto-send to orchestrator when threshold is met
  useEffect(() => {
    console.log('🔍 Auto-send check:', {
      hasJudgeResult: !!judgeResult,
      judgeAutoSend,
      score: judgeResult?.score,
      threshold: judgeThreshold,
      meetsThreshold: judgeResult ? judgeResult.score >= judgeThreshold : false,
      approvalSent,
    })

    if (judgeResult && judgeAutoSend && judgeResult.score >= judgeThreshold && !approvalSent) {
      console.log('✅ Auto-sending approval to orchestrator')

      const approvalMessage = `[APPROVAL_NOTIFICATION]
packet_type:APPROVAL_NOTIFICATION
timestamp:${new Date().toISOString()}

[STATUS]
threshold_met:yes
required_score:${judgeThreshold.toFixed(2)}
achieved_score:${judgeResult.score.toFixed(2)}
gap:${(judgeResult.score - judgeThreshold).toFixed(2)}

[JUDGE_RESULT]
${judgeResult.reasoning}

[USER_NOTES]
${judgeNotes || 'No additional notes.'}

[CONCLUSION]
Quality threshold has been met. No further revisions needed unless user requests specific changes.
Work is approved for completion.`

      // Use setTimeout to ensure this runs after the current render cycle
      setTimeout(async () => {
        console.log('📤 Sending approval message to orchestrator...')
        await sendMessage(approvalMessage)
        console.log('✅ Approval message sent, setting approvalSent=true')
        setApprovalSent(true) // Mark approval as sent to prevent loop
      }, 100)
    } else if (judgeResult && judgeAutoSend && judgeResult.score >= judgeThreshold && approvalSent) {
      console.log('⏭️ Skipping auto-send: approval already sent')
    } else if (judgeResult && judgeAutoSend && judgeResult.score < judgeThreshold) {
      console.log('⚠️ Skipping auto-send: score below threshold')
    } else if (judgeResult && !judgeAutoSend) {
      console.log('⏸️ Skipping auto-send: auto-send is disabled')
    }
  }, [judgeResult, judgeAutoSend, judgeThreshold, approvalSent, judgeNotes, sendMessage, setApprovalSent])

  // Close project menu on click outside
  const projectMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (showProjectMenu && projectMenuRef.current && !projectMenuRef.current.contains(e.target as Node)) {
        setShowProjectMenu(false)
      }
    }

    if (showProjectMenu) {
      // Delay to avoid immediate close from the button click
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside)
      }, 0)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [showProjectMenu])

  const handleMouseDown = (side: 'left' | 'right') => (e: React.MouseEvent) => {
    e.preventDefault()
    if (side === 'left') setIsResizingLeft(true)
    else setIsResizingRight(true)
  }

  // Handle peer resize
  const handlePeerResize = (e: MouseEvent) => {
    if (resizingPeer) {
      const deltaY = e.movementY
      setPeerHeights((prev) => ({
        ...prev,
        [resizingPeer]: Math.max(100, Math.min(prev[resizingPeer as keyof typeof prev] + deltaY, 600)),
      }))
    }
  }

  const handlePeerMouseUp = () => {
    setResizingPeer(null)
  }

  // Add/remove peer resize event listeners
  useEffect(() => {
    if (resizingPeer) {
      document.addEventListener('mousemove', handlePeerResize)
      document.addEventListener('mouseup', handlePeerMouseUp)
      return () => {
        document.removeEventListener('mousemove', handlePeerResize)
        document.removeEventListener('mouseup', handlePeerMouseUp)
      }
    }
  }, [resizingPeer])

  // Show login screen if not authenticated
  if (!user) {
    return <LoginScreen />
  }

  if (showFirstRunWizard) {
    return <FirstRunWizard onComplete={() => setShowFirstRunWizard(false)} />
  }

  return (
    <div className="flex flex-col h-screen bg-background text-foreground overflow-hidden">
      <nav className="h-16 bg-background-secondary border-b border-border flex items-center justify-between px-4">
        {/* Left: Logo and Branding */}
        <div className="flex items-center gap-3">
          <img src={hivemindLogo} alt="HiveMind Logo" className="w-10 h-10" style={{ filter: 'brightness(0) invert(1)' }} />
          <div className="flex flex-col">
            <span className="font-title text-2xl">HiveMind</span>
            <span className="font-bold text-xs text-cyan-400" style={{ fontFamily: 'Doto, monospace' }}>
              by &lt;&#123;[ALIENROBOT]&#125;&gt;
            </span>
          </div>
        </div>

        {/* Center: Load Project */}
        <div className="flex items-center gap-2 relative" ref={projectMenuRef}>
          <button
            className="bg-background-tertiary border border-border rounded px-4 py-2 text-sm flex items-center gap-2 hover:bg-background-hover"
            onClick={(e) => {
              e.stopPropagation()
              setShowProjectMenu(!showProjectMenu)
            }}
            title="Select or create a project"
          >
            <FolderOpen className="w-4 h-4" />
            <span>{currentProject?.name || 'Load Project'}</span>
            <ChevronDown className="w-4 h-4" />
          </button>

          {/* GitHub sync button */}
          {currentProject && <GitHubSyncButton project={currentProject} />}

          {/* Git button - show for GitHub projects or file system projects (repos) */}
          {(currentProject?.github || currentProject?.fileSystemPath) && (
            <button
              className="bg-background-tertiary border border-border rounded px-3 py-2 text-xs flex items-center gap-1 hover:bg-background-hover"
              onClick={() => setShowGitCommit(true)}
              title="Commit and push changes"
            >
              <GitCommit className="w-3.5 h-3.5" />
              Git
            </button>
          )}

          {showProjectMenu && (
            <div className="absolute top-full left-0 mt-1 bg-background-secondary border border-border rounded shadow-lg min-w-[200px] z-50">
              <div className="p-2 border-b border-border space-y-2">
                <button
                  className="btn-primary w-full px-3 py-2 text-xs"
                  onClick={() => {
                    setShowNewProject(true)
                    setShowProjectMenu(false)
                  }}
                >
                  + New Project
                </button>
                <button
                  className="btn-secondary w-full px-3 py-2 text-xs"
                  onClick={() => {
                    setShowLoadRepo(true)
                    setShowProjectMenu(false)
                  }}
                >
                  📁 Load Repository
                </button>
                <button
                  className="btn-secondary w-full px-3 py-2 text-xs"
                  onClick={() => {
                    setShowGitHubBrowser(true)
                    setShowProjectMenu(false)
                  }}
                >
                  🐙 Clone from GitHub
                </button>
              </div>
              <div className="max-h-[300px] overflow-auto">
                {projects.map((project) => (
                  <button
                    key={project.id}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-background-hover ${currentProject?.id === project.id ? 'bg-background-tertiary' : ''
                      }`}
                    onClick={() => {
                      openProject(project.id)
                      setShowProjectMenu(false)
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-1">
                          {project.name}
                          {project.github && (
                            <span className="text-[10px] text-accent" title={`GitHub: ${project.github.owner}/${project.github.repo}`}>
                              🐙
                            </span>
                          )}
                        </div>
                        <div className="text-foreground-muted text-[10px]">
                          {new Date(project.updatedAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
                {projects.length === 0 && (
                  <div className="px-3 py-4 text-xs text-foreground-muted text-center">
                    No projects yet
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          {currentProject && (
            <button
              onClick={() => setShowShareModal(true)}
              className="p-2 hover:bg-background-tertiary rounded-full text-foreground-muted hover:text-accent transition-colors"
              title="Share Project"
            >
              <Share2 className="w-5 h-5" />
            </button>
          )}

          <button
            className="btn-ghost px-3 py-2 text-sm flex items-center gap-2"
            onClick={() => saveCurrentProject()}
            title="Save project"
          >
            <Save className="w-4 h-4" />
            Save
          </button>

          {/* Save to Cloud Button */}
          {user && currentProject && currentProject.storageType !== 'cloud' && (
            <button
              className="btn-secondary px-3 py-2 text-sm flex items-center gap-2 text-accent"
              onClick={() => saveToCloud()}
              title="Upload project to cloud for backup and syncing"
            >
              <CloudUpload className="w-4 h-4" />
              Save to Cloud
            </button>
          )}
          <button
            className="btn-ghost p-2"
            onClick={() => setShowUserGuide(true)}
            title="User Guide - Learn how to use HiveMind"
          >
            <BookOpen className="w-5 h-5" />
          </button>
          <button
            className="btn-ghost p-2"
            onClick={() => setShowSettings(true)}
            title="Settings - Configure API keys, models, and workspace"
          >
            <Settings className="w-5 h-5" />
          </button>
          <div className="w-px h-4 bg-border mx-1" />
          <AuthButton />
        </div>
      </nav>

      <main ref={containerRef} className="flex-1 flex overflow-hidden relative">
        {/* Blocking overlay when no project */}
        {!currentProject && hasSeenOnboarding && (
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm z-40 flex items-center justify-center">
            <div className="bg-background-secondary border border-border rounded-lg p-8 max-w-md text-center">
              <FolderOpen className="w-16 h-16 text-accent mx-auto mb-4" />
              <h2 className="text-2xl font-title mb-3">No Project Loaded</h2>
              <p className="text-foreground-muted mb-6">
                Create a new project or open an existing repository to get started.
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => setShowNewProject(true)}
                  className="btn-primary px-6 py-3 flex items-center justify-center gap-2"
                >
                  <Brain className="w-4 h-4" />
                  Create New Project
                </button>
                {projects.length > 0 && (
                  <button
                    onClick={() => setShowProjectMenu(true)}
                    className="btn-secondary px-6 py-2 flex items-center justify-center gap-2"
                  >
                    <FolderOpen className="w-4 h-4" />
                    Open Existing Project ({projects.length})
                  </button>
                )}
                <button
                  onClick={() => setShowLoadRepo(true)}
                  className="btn-ghost px-6 py-2 flex items-center justify-center gap-2"
                >
                  <FolderOpen className="w-4 h-4" />
                  Open Local Repository
                </button>
                <button
                  onClick={() => setShowGitHubBrowser(true)}
                  className="btn-ghost px-6 py-2 flex items-center justify-center gap-2"
                >
                  🐙 Clone from GitHub
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Left Sidebar */}
        <LeftSidebar
          width={leftWidth}
          currentProject={currentProject}
          onFileDoubleClick={(filePath, content) => setFileViewModal({ filePath, content })}
          refreshTrigger={fileListRefreshTrigger}
          highlightFile={highlightFile}
        />

        {/* Left Resize Handle */}
        <div
          className="w-1 cursor-col-resize hover:bg-accent/20 active:bg-accent/30 flex-shrink-0"
          onMouseDown={handleMouseDown('left')}
        />

        {/* Center Panel */}
        <CenterPanel
          activeTab={activeTab}
          chatInput={chatInput}
          messages={messages}
          isLoading={isLoading}
          isOrchestrating={isOrchestrating}
          autoPeerReview={autoPeerReview}
          includeDocs={includeDocs}
          inflatedMarkdown={inflatedMarkdown}
          isInflating={isInflating}
          isExportingPDF={isExportingPDF}
          selectedInflatedDoc={selectedInflatedDoc}
          inflatedDocs={inflatedDocs}
          copiedInflate={copiedInflate}
          copiedPreview={copiedPreview}
          judgeResult={judgeResult}
          metricsHistory={metricsHistory}
          cumulativeCost={chatSessionCost + peerCumulativeCost}
          cumulativeTokens={chatSessionTokens + peerCumulativeTokens}
          rubricContent={currentProject?.files['rubric.moth']}
          peers={peers}
          judgeModel={currentProject?.settings?.models?.judge}
          hasProject={!!currentProject}
          onSetActiveTab={setActiveTab}
          onSetChatInput={setChatInput}
          onSendMessage={sendMessage}

          onStopStreaming={stopStreaming}
          onSetAutoPeerReview={setAutoPeerReview}
          onSetIncludeDocs={setIncludeDocs}
          onRunOrchestration={runOrchestration}
          onSaveOrchestratorChat={saveOrchestratorChat}
          onCopyPeerResponse={copyPeerResponse}
          onInflatePeerResponse={inflatePeerResponse}
          onSaveChatMessage={saveChatMessage as any}
          onSetApprovalSent={setApprovalSent}
          onHandleInflate={handleInflate}
          onHandleLoadInflatedDoc={handleLoadInflatedDoc}
          onCopyInflatedMarkdown={copyInflatedMarkdown}
          onExportPDF={exportPDF}
          previewRef={previewRef}
          onFilesChanged={() => setFileListRefreshTrigger(prev => prev + 1)}
          onHighlightFile={setHighlightFile}
          onOpenFile={handleOpenFile}
        />

        {/* Right Resize Handle - only show when not collapsed */}
        {!isRightPanelCollapsed && (
          <div
            className="w-1 cursor-col-resize hover:bg-accent/20 active:bg-accent/30 flex-shrink-0"
            onMouseDown={handleMouseDown('right')}
          />
        )}

        {/* Right Sidebar */}
        {!isRightPanelCollapsed && (
          <RightSidebar
            width={rightWidth}
            peers={peers}
            peerResponses={peerResponses}
            peerHeights={peerHeights}
            judgeResult={judgeResult}
            judgeAutoSend={judgeAutoSend}
            judgeThreshold={judgeThreshold}
            judgeNotes={judgeNotes}
            isEditingThreshold={isEditingThreshold}
            approvalSent={approvalSent}
            isOrchestrating={isOrchestrating}
            draggedPeer={draggedPeer}
            currentProject={currentProject}
            messages={messages}
            isCollapsed={isRightPanelCollapsed}
            onToggleCollapse={() => setIsRightPanelCollapsed(!isRightPanelCollapsed)}
            onTogglePeerCollapse={togglePeerCollapse}
            onTogglePeerEnabled={togglePeerEnabled}
            onOpenPeerSettings={setEditingPeerId}
            onCopyPeerResponse={copyPeerResponse}
            onInflatePeerResponse={inflatePeerResponse}
            onSavePeerChat={savePeerChat}
            onSendPeerToOrchestrator={sendPeerToOrchestrator}
            onExpandPeer={setExpandedPeer}
            onPeerDragStart={setDraggedPeer}
            onPeerDrop={handlePeerDrop}
            onPeerDragEnd={() => setDraggedPeer(null)}
            onResizePeerStart={setResizingPeer}
            onToggleJudgeAutoSend={() => setJudgeAutoSend(!judgeAutoSend)}
            onSetJudgeThreshold={setJudgeThreshold}
            onSetJudgeNotes={setJudgeNotes}
            onSetEditingThreshold={setIsEditingThreshold}
            onApproveJudge={handleApprove}
            onRequestRevisions={requestRevisions}
            onCopyJudge={copyJudgeResult}
            onInflateJudge={inflateJudgeResult}
            onSaveJudge={saveJudgeResult}
            onSendJudgeToOrchestrator={sendJudgeToOrchestrator}
            onExpandJudge={() => setExpandedPeer('judge')}
            onResizeJudgeStart={() => setResizingPeer('judge')}
          />
        )}

        {/* Collapsed Right Panel Toggle Button */}
        {isRightPanelCollapsed && (
          <div className="flex-shrink-0 bg-background-secondary border-l border-border flex items-center justify-center w-8">
            <button
              onClick={() => setIsRightPanelCollapsed(false)}
              className="p-2 hover:bg-background-hover rounded transition-colors rotate-180"
              title="Show peer review panel"
            >
              <ChevronDown className="w-4 h-4 transform -rotate-90" />
            </button>
          </div>
        )}
      </main>

      <footer className="h-8 bg-background-secondary border-t border-border flex items-center justify-between px-3">
        <div className="flex items-center gap-3 text-xs text-foreground-muted">
          <div className="flex items-center gap-1">
            <Cpu className="w-3 h-3" />
            <span>Tokens {(chatSessionTokens + peerCumulativeTokens).toLocaleString()}</span>
          </div>
          <div className="px-2 py-0.5 bg-background-tertiary rounded text-accent">
            ${(chatSessionCost + peerCumulativeCost).toFixed(4)}
          </div>
          {chatLastLatency > 0 && (
            <span>Latency {(chatLastLatency / 1000).toFixed(1)}s</span>
          )}
        </div>
        <div className="flex items-center gap-4 text-xs text-foreground-muted">
          <div className="flex items-center gap-2">
            <span>Copyright 2025 John Huikku Alienrobot LLC</span>
            <a href="https://www.alienrobot.com" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">www.ALIENROBOT.com</a>
          </div>
          <a
            href="https://ko-fi.com/alienrobot"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-foreground-muted hover:text-accent transition-colors"
            title="Support HiveMind development"
          >
            <Coffee className="w-3.5 h-3.5" />
            <span>Buy me a coffee</span>
          </a>
        </div>
        <div className="flex gap-2">
          <button
            className="btn-ghost p-1"
            title="Undo last action (coming soon)"
          >
            <Undo className="w-3 h-3" />
          </button>
          <button
            className="btn-ghost p-1"
            title="Redo last action (coming soon)"
          >
            <Redo className="w-3 h-3" />
          </button>
        </div>
      </footer>

      {/* Settings Modal */}
      {showShareModal && currentProject && (
        <ShareModal
          projectId={currentProject.id}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {showSettings && (
        <SettingsModal
          onClose={() => setShowSettings(false)}
          highPriority={showOnboarding} // Render above onboarding modal if both are open
        />
      )}

      {/* User Guide Modal */}
      {showUserGuide && <UserGuideModal onClose={() => setShowUserGuide(false)} />}

      {/* Add Peer Modal */}
      {showAddPeer && (
        <AddPeerModal
          onClose={() => setShowAddPeer(false)}
          onAdd={(persona, modelId) => {
            // TODO: Implement actual peer addition
            alert(`Adding custom peers is coming soon!\n\nSelected: ${persona.name}\nModel: ${modelId}\n\nFor now, you can customize the existing peers in Settings.`)
            setShowAddPeer(false)
          }}
        />
      )}

      {/* Peer Settings Modal */}
      {editingPeerId && (() => {
        const peerToEdit = peers.find(p => p.id === editingPeerId)
        if (!peerToEdit) return null

        return (
          <PeerSettingsModal
            peer={peerToEdit}
            onClose={() => setEditingPeerId(null)}
            onSave={(updatedPeer) => {
              const updatedPeers = peers.map(p =>
                p.id === editingPeerId ? updatedPeer : p
              )
              updateProjectSettings({
                ...currentProject?.settings,
                peers: updatedPeers
              })
              setEditingPeerId(null)
            }}
          />
        )
      })()}

      {/* Peer Modals */}
      {expandedPeer === 'gemini' && (
        <PeerModal
          peer={{ id: 'gemini', name: 'Gemini_Reviewer', role: 'Technical Reviewer', color: 'purple', icon: 'Diamond' }}
          response={peerResponses.gemini}
          onClose={() => setExpandedPeer(null)}
          onSave={(editedContent) => {
            // Save edited content to file
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
            updateFile(`peers/gemini-edited-${timestamp}.moth`, editedContent)
          }}
        />
      )}
      {expandedPeer === 'claude' && (
        <PeerModal
          peer={{ id: 'claude', name: 'Claude_Editor', role: 'Content Editor', color: 'orange', icon: 'Shield' }}
          response={peerResponses.claude}
          onClose={() => setExpandedPeer(null)}
          onSave={(editedContent) => {
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
            updateFile(`peers/claude-edited-${timestamp}.moth`, editedContent)
          }}
        />
      )}
      {expandedPeer === 'grok' && (
        <PeerModal
          peer={{ id: 'grok', name: 'Grok_Innovator', role: 'Innovation Advisor', color: 'green', icon: 'Rocket' }}
          response={peerResponses.grok}
          onClose={() => setExpandedPeer(null)}
          onSave={(editedContent) => {
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
            updateFile(`peers/grok-edited-${timestamp}.moth`, editedContent)
          }}
        />
      )}
      {expandedPeer === 'judge' && judgeResult && (
        <PeerModal
          peer={{ id: 'judge', name: 'Judge', role: 'Quality Assessor', color: 'yellow', icon: 'Scale' }}
          response={{
            personaId: 'judge',
            personaName: 'Judge',
            content: `Score: ${judgeResult.score.toFixed(2)}\n\n${judgeResult.reasoning}`,
            timestamp: judgeResult.timestamp,
          }}
          onClose={() => setExpandedPeer(null)}
          onSave={(editedContent) => {
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
            updateFile(`judge/judge-edited-${timestamp}.moth`, editedContent)
          }}
        />
      )}

      {/* New Project Modal */}
      {showNewProject && (
        <ProjectSetupModal
          onComplete={(projectName, peers, orchestratorModel, judgeModel) => {
            createProject(projectName, peers)
            setShowNewProject(false)

            // Update orchestrator and judge models if provided
            if (currentProject && (orchestratorModel || judgeModel)) {
              const updates: any = { ...currentProject.settings }
              if (!updates.models) {
                updates.models = {}
              }
              if (orchestratorModel) {
                updates.models.orchestrator = orchestratorModel
              }
              if (judgeModel) {
                updates.models.judge = judgeModel
              }
              updateProjectSettings(updates)
            }
          }}
          onCancel={() => setShowNewProject(false)}
        />
      )}

      {/* Workspace Setup Modal */}
      {showWorkspaceSetup && (
        <WorkspaceSetupModal
          onWorkspaceSelected={async (dirHandle) => {
            await saveWorkspaceHandle(dirHandle)
            setShowWorkspaceSetup(false)
            // Reload projects from new workspace
            await loadProjects()
          }}
          onSkip={() => {
            setShowWorkspaceSetup(false)
            // Continue with IndexedDB mode
            loadProjects()
          }}
        />
      )}

      {/* Load Repository Modal */}
      {showLoadRepo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowLoadRepo(false)}>
          <div className="bg-background-secondary border border-border rounded-lg w-[500px]" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-border p-4">
              <h2 className="text-lg font-title">Load Repository</h2>
            </div>

            <div className="p-4 space-y-4">
              <div className="text-sm text-foreground-secondary space-y-3">
                <p>
                  Load a git repository to work with its files directly in HiveMind.
                </p>
                <p>
                  You'll be prompted to select a folder on your computer. HiveMind will:
                </p>
                <ul className="list-disc list-inside space-y-1 text-xs ml-2">
                  <li>Read files from the repository</li>
                  <li>Save new files (MOTH packets, inflated docs, etc.)</li>
                  <li>Keep all changes synced to the folder</li>
                </ul>
                <p className="text-xs text-foreground-muted">
                  Note: This uses the File System Access API, which requires permission
                  and works in modern browsers (Chrome, Edge, etc.)
                </p>
              </div>

              <div className="flex justify-end gap-2">
                <button className="btn-secondary px-4 py-2" onClick={() => {
                  setShowLoadRepo(false)
                }}>
                  Cancel
                </button>
                <button
                  className="btn-primary px-4 py-2"
                  onClick={async () => {
                    setShowLoadRepo(false)
                    await loadRepoAsProject('')
                  }}
                >
                  📁 Choose Folder
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Onboarding Modal */}
      {showOnboarding && (
        <OnboardingModal
          onComplete={() => setShowOnboarding(false)}
          onCreateProject={() => {
            setShowOnboarding(false)
            setShowNewProject(true)
          }}
          onOpenRepo={() => {
            setShowOnboarding(false)
            setShowLoadRepo(true)
          }}
          onOpenGitHubRepo={() => {
            setShowOnboarding(false)
            setShowGitHubBrowser(true)
          }}
          onSelectProject={async (projectId) => {
            setShowOnboarding(false)
            await openProject(projectId)
          }}
          onOpenSettings={() => setShowSettings(true)}
          hasApiKeys={hasAnyApiKey()}
          hasGitHubToken={!!getApiKey('github')}
        />
      )}

      {/* File View Modal */}
      {fileViewModal && (
        <FileViewModal
          filePath={fileViewModal.filePath}
          content={fileViewModal.content}
          highlightLines={fileViewModal.highlightLines}
          onClose={() => setFileViewModal(null)}
          onSave={async (filePath, newContent) => {
            await updateFile(filePath, newContent)
            setFileViewModal(null)
          }}
        />
      )}

      {/* GitHub Repository Browser */}
      {showGitHubBrowser && (
        <GitHubRepoBrowser
          isOpen={showGitHubBrowser}
          onClose={() => setShowGitHubBrowser(false)}
          onSelectRepo={async (repoUrl: string, repoName: string) => {
            try {
              console.log('[App] Starting GitHub clone:', repoUrl, 'as', repoName)
              await loadGitHubRepoAsProject(repoUrl, repoName, (message, progress) => {
                console.log(`[GitHub Clone] ${message} (${progress}%)`)
              })
              console.log('[App] Clone completed')
              // Only close if no duplicate was detected (duplicate modal would be showing)
              const { githubDuplicateInfo, currentProject } = useProjectStore.getState()
              console.log('[App] After clone - currentProject:', currentProject?.name, 'duplicate?', !!githubDuplicateInfo)
              if (!githubDuplicateInfo) {
                setShowGitHubBrowser(false)
              }
            } catch (error) {
              console.error('[App] Failed to clone repository:', error)
              // Error is already set in the store
            }
          }}
        />
      )}

      {/* GitHub Duplicate Detection Modal */}
      {githubDuplicateInfo && (
        <GitHubDuplicateModal
          existingProjectName={githubDuplicateInfo.existingProject.name}
          repoFullName={githubDuplicateInfo.repoInfo.fullName}
          onOpenExisting={() => {
            handleGitHubDuplicateAction('open')
            setShowGitHubBrowser(false)
          }}
          onUpdate={() => {
            handleGitHubDuplicateAction('update')
            setShowGitHubBrowser(false)
          }}
          onCloneNew={() => {
            handleGitHubDuplicateAction('new')
            setShowGitHubBrowser(false)
          }}
          onCancel={() => {
            setGitHubDuplicateInfo(null)
            setShowGitHubBrowser(false)
          }}
        />
      )}

      {/* Error Modal */}
      {errorDetails && (
        <ErrorModal
          isOpen={showErrorModal}
          title={errorDetails.title}
          message={errorDetails.message}
          errorType={errorDetails.type}
          actionUrl={errorDetails.actionUrl}
          actionLabel={errorDetails.actionLabel}
          onClose={() => {
            setShowErrorModal(false)
            setErrorDetails(null)
          }}
        />
      )}

      {/* Git Commit Modal */}
      {showGitCommit && currentProject?.github && (
        <GitCommitModal
          repoUrl={currentProject.github.url}
          branch={currentProject.github.branch}
          files={Object.entries(currentProject.files).map(([path, content]) => ({
            path,
            content,
          }))}
          onClose={() => setShowGitCommit(false)}
          onSuccess={() => {
            // Optionally refresh the project or show a success message
            console.log('Successfully committed and pushed changes')
          }}
        />
      )}

      {/* Git Tool Confirmation Modal (for LLM-initiated git operations) */}
      {pendingGitAction && (
        <GitToolConfirmModal
          action={pendingGitAction}
          onConfirm={handleConfirmGitAction}
          onCancel={handleCancelGitAction}
        />
      )}

      {/* File Tool Confirmation Modal (for LLM-initiated file operations) */}
      {pendingFileAction && (
        <FileToolConfirmModal
          action={pendingFileAction}
          onConfirm={handleConfirmFileAction}
          onCancel={handleCancelFileAction}
        />
      )}
    </div>
  )
}

export default App
